import { supabase } from './serviceService.js';

const TABLE = 'faq_items';
// Cache FAQ to avoid re-sorting on every render.
let faqItemsCache = null;
let faqInFlightPromise = null;
const FAQ_CACHE_KEY = 'rb_faq_cache_v1';
const FAQ_CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const readFaqStorageCache = () => {
  if (!canUseStorage()) return null;
  try {
    const raw = window.localStorage.getItem(FAQ_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return { items: parsed, isFresh: true };
    }
    const items = Array.isArray(parsed?.items) ? parsed.items : null;
    const savedAt = Number(parsed?.savedAt || 0);
    if (!items) return null;
    const isFresh = savedAt > 0 ? Date.now() - savedAt < FAQ_CACHE_MAX_AGE_MS : true;
    return { items, isFresh };
  } catch (_) {
    return null;
  }
};

const writeFaqStorageCache = (items) => {
  if (!canUseStorage() || !Array.isArray(items)) return;
  try {
    window.localStorage.setItem(FAQ_CACHE_KEY, JSON.stringify({
      savedAt: Date.now(),
      items,
    }));
  } catch (_) {
  }
};

const mapDbToModel = (row) => ({
  id: row.id,
  question: row.question ?? '',
  answer: row.answer ?? '',
  sortOrder: row.sort_order ?? 0,
  isActive: row.is_active ?? true,
  createdAt: row.created_at,
  updatedAt: row.updated_at,
});

const mapModelToDb = (data) => ({
  question: data.question ?? null,
  answer: data.answer ?? null,
  sort_order: Number.isFinite(Number(data.sortOrder)) ? Number(data.sortOrder) : 0,
  is_active: typeof data.isActive === 'boolean' ? data.isActive : true,
});

const fetchFaqItems = async () => {
  const { data, error } = await supabase
    .from(TABLE)
    .select('*')
    .order('sort_order', { ascending: true })
    .order('id', { ascending: true });

  if (error) throw error;
  const mapped = (data || []).map(mapDbToModel);
  writeFaqStorageCache(mapped);
  return mapped;
};

export const faqService = {
  getCached: () => faqItemsCache,

  getAll: async ({ force = false } = {}) => {
    if (!force && Array.isArray(faqItemsCache)) {
      return faqItemsCache;
    }

    if (!force) {
      const storageCache = readFaqStorageCache();
      if (Array.isArray(storageCache?.items) && storageCache.items.length > 0 && storageCache.isFresh) {
        faqItemsCache = storageCache.items;
        return storageCache.items;
      }
    }

    if (!force && faqInFlightPromise) {
      return faqInFlightPromise;
    }

    faqInFlightPromise = fetchFaqItems()
      .then((items) => {
        faqItemsCache = items;
        return items;
      })
      .finally(() => {
        faqInFlightPromise = null;
      });

    return faqInFlightPromise;
  },

  warmup: async () => {
    try {
      await faqService.getAll();
    } catch (_) {
    }
  },

  create: async (data) => {
    const payload = mapModelToDb(data);
    const { data: result, error } = await supabase
      .from(TABLE)
      .insert(payload)
      .select()
      .single();

    if (error) throw error;
    const created = mapDbToModel(result);
    if (Array.isArray(faqItemsCache)) {
      faqItemsCache = [...faqItemsCache, created].sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
      writeFaqStorageCache(faqItemsCache);
    }
    return created;
  },

  update: async (id, data) => {
    const payload = mapModelToDb(data);
    const { data: result, error } = await supabase
      .from(TABLE)
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    const updated = mapDbToModel(result);
    if (Array.isArray(faqItemsCache)) {
      faqItemsCache = faqItemsCache
        .map((item) => (item.id === id ? updated : item))
        .sort((a, b) => a.sortOrder - b.sortOrder || a.id - b.id);
      writeFaqStorageCache(faqItemsCache);
    }
    return updated;
  },

  delete: async (id) => {
    const { error } = await supabase
      .from(TABLE)
      .delete()
      .eq('id', id);

    if (error) throw error;
    if (Array.isArray(faqItemsCache)) {
      faqItemsCache = faqItemsCache.filter((item) => item.id !== id);
      writeFaqStorageCache(faqItemsCache);
    }
  },
};
